import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Text,
  Textarea,
  VStack,
  Menu,
  MenuButton,
  MenuList,
} from '@chakra-ui/react';
import { AddIcon, ChevronDownIcon, CloseIcon, MinusIcon, SmallAddIcon } from '@chakra-ui/icons';
import { FaBolt } from 'react-icons/fa';
import ModalShell from '../../../../../../components/ui/ModalShell';
import { ff } from '../../../../../../theme';
import type { LocalCreatorsPackageRow } from '../../types';
import { InstagramPlatformChip, TikTokPlatformChip, UgcPackageIcon } from '../package/PackagePlatformChips';
import { lineLabel, type PlatformId, type TypeId } from '../../lib/packageContentTypes';
import {
  deliverablesToContentLines,
  linesToDeliverables,
  type PackageContentLine,
} from '../../lib/packageMapping';
import {
  PackageContentTypeMenuItems,
  PACKAGE_CONTENT_TYPE_MENU_LIST_PROPS,
} from '../package/PackageContentTypeMenuItems';

const CAROUSEL_TYPES: TypeId[] = ['carousel', 'photoCarousel', 'ugcPhotoCarousel'];
const isCarouselType = (t: TypeId | ''): t is TypeId =>
  t !== '' && (CAROUSEL_TYPES as readonly string[]).includes(t);

export interface CreatePackageModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (row: LocalCreatorsPackageRow) => void;
  /** When set, we edit this package (same id on save). */
  editingPackage?: LocalCreatorsPackageRow | null;
}

type ContentLine = PackageContentLine;

const newLineId = () => `line-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

/** Strips a display price like "$20" or "$19.5" to digits for the price input. */
function priceDisplayToInputDigits(display: string): string {
  const t = display.trim();
  if (!t) {
    return '';
  }
  const noCurrency = t.replace(/^\$/, '').replace(/,/g, '').trim();
  if (/^\d*\.?\d*$/.test(noCurrency)) {
    return noCurrency;
  }
  const parsed = parseFloat(noCurrency);
  if (Number.isFinite(parsed)) {
    return String(parsed);
  }
  return '';
}

type EditableLine = Omit<ContentLine, 'type'> & { type: TypeId | '' };

const createDefaultLine = (): EditableLine => ({
  id: newLineId(),
  platform: 'instagram',
  type: '',
  quantity: 1,
});

const CreatePackageModal: React.FC<CreatePackageModalProps> = ({ isOpen, onClose, onSave, editingPackage }) => {
  const [lines, setLines] = useState<EditableLine[]>(() => [createDefaultLine()]);
  const [description, setDescription] = useState('');
  const [priceDigits, setPriceDigits] = useState('');

  const resetToNew = useCallback(() => {
    setLines([createDefaultLine()]);
    setDescription('');
    setPriceDigits('');
  }, []);

  const applyEditing = useCallback((pkg: LocalCreatorsPackageRow) => {
    const fromDeliverables = deliverablesToContentLines(pkg.deliverables);
    setLines(fromDeliverables.length > 0 ? fromDeliverables : [createDefaultLine()]);
    setDescription(pkg.description);
    setPriceDigits(priceDisplayToInputDigits(pkg.price));
  }, []);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    if (editingPackage) {
      applyEditing(editingPackage);
    } else {
      resetToNew();
    }
  }, [isOpen, editingPackage, applyEditing, resetToNew]);

  const displayPrice = useMemo(() => {
    const n = parseFloat(priceDigits);
    if (!Number.isFinite(n) || priceDigits === '' || priceDigits === '.') {
      return '$0';
    }
    return n % 1 === 0 ? `$${n}` : `$${n}`;
  }, [priceDigits]);

  const canSubmit = useMemo(() => {
    if (lines.length === 0) return false;
    if (lines.some((l) => l.type === '')) return false;
    if (!lines.some((l) => l.quantity >= 1)) return false;
    const n = parseFloat(priceDigits);
    if (priceDigits.trim() === '' || !Number.isFinite(n) || n < 0) return false;
    return true;
  }, [lines, priceDigits]);

  const canAddDeliverable = useMemo(() => lines.every((l) => l.type !== ''), [lines]);

  const makeRow = useCallback((): LocalCreatorsPackageRow => {
    const desc = description.trim();
    const selectedLines = lines.filter(
      (l): l is Omit<EditableLine, 'type'> & { type: TypeId } => l.type !== ''
    );
    return {
      id: editingPackage?.id ?? `pkg-${Date.now()}`,
      price: displayPrice,
      description: desc,
      featured: editingPackage?.featured ?? false,
      deliverables: linesToDeliverables(selectedLines),
    };
  }, [description, displayPrice, lines, editingPackage?.id, editingPackage?.featured]);

  const handleAddLine = (platform: PlatformId, type: TypeId) => {
    if (!lines.every((l) => l.type !== '')) {
      return;
    }
    setLines((prev) => [
      ...prev,
      { id: newLineId(), platform, type, quantity: 1, ...(isCarouselType(type) ? { count: 2 } : {}) },
    ]);
  };

  const updateLine = (id: string, patch: Partial<Omit<ContentLine, 'id'>>) => {
    setLines((prev) =>
      prev.map((l) => {
        if (l.id !== id) return l;
        const next = { ...l, ...patch };
        if (typeof patch.quantity === 'number') next.quantity = Math.max(1, patch.quantity);
        if (typeof patch.count === 'number') next.count = Math.max(2, patch.count);
        return next;
      })
    );
  };

  const bumpQty = (id: string, delta: number) => {
    setLines((prev) =>
      prev.map((l) => {
        if (l.id !== id) return l;
        if (isCarouselType(l.type)) {
          return { ...l, count: Math.max(2, (l.count ?? 2) + delta) };
        }
        return { ...l, quantity: Math.max(1, l.quantity + delta) };
      })
    );
  };

  const removeLine = (id: string) => {
    setLines((prev) => {
      if (prev.length <= 1) return prev;
      return prev.filter((l) => l.id !== id);
    });
  };

  const handleTypeSelect = (id: string, t: TypeId) => {
    const platform: PlatformId =
      t === 'video' || t === 'photoCarousel'
        ? 'tiktok'
        : t.startsWith('ugc')
          ? 'ugc'
          : 'instagram';
    updateLine(id, {
      type: t,
      platform,
      quantity: 1,
      ...(isCarouselType(t) ? { count: 2 } : { count: undefined }),
    });
  };

  const handleSavePackage = () => {
    if (!canSubmit) return;
    onSave(makeRow());
    onClose();
  };

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={onClose}
      title={editingPackage ? 'Edit package' : 'Create a package'}
      footer={
        // Sticky so Save stays reachable while the form scrolls; the dialog container is the scroller.
        <Flex
          position="sticky"
          bottom={0}
          gap={3}
          mt={5}
          mx={-5}
          px={5}
          pt="14px"
          pb="env(safe-area-inset-bottom)"
          bg="white"
          borderTop={`1px solid ${ff.line}`}
        >
          <Button variant="inkOutline" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="lime" flex={1} isDisabled={!canSubmit} onClick={handleSavePackage}>
            {editingPackage ? 'Save changes' : 'Add package'}
          </Button>
        </Flex>
      }
    >
      <VStack align="stretch" spacing={6}>
        <Flex gap="12px" p="14px" borderRadius="20px" bg={ff.limeSurface} align="flex-start">
          <Flex
            w="28px"
            h="28px"
            borderRadius="full"
            bg={ff.lime}
            align="center"
            justify="center"
            color={ff.ink}
            flexShrink={0}
          >
            <FaBolt size={12} />
          </Flex>
          <Text fontSize="14px" lineHeight="1.45" color={ff.body}>
            <Text as="span" fontWeight={600} color={ff.ink}>
              On-demand rate.
            </Text>{' '}
            Brands can purchase this package instantly as an order — no negotiation needed.
          </Text>
        </Flex>

        <Box>
          <Text as="h4" textStyle="eyebrow" color={ff.text2} mb="10px">
            1 · Content type
          </Text>
          <VStack align="stretch" spacing="10px">
            {lines.map((line) => (
              <Flex
                key={line.id}
                minH="56px"
                pl="14px"
                pr="10px"
                align="center"
                justify="space-between"
                gap={2}
                borderRadius="14px"
                border="1px solid"
                borderColor={ff.border}
                bg="white"
              >
                <Box flex="1" minW={0}>
                  <Menu placement="bottom-start" strategy="fixed" isLazy>
                    <MenuButton
                      as={Button}
                      type="button"
                      variant="unstyled"
                      display="flex"
                      alignItems="center"
                      w="100%"
                      minW={0}
                      h="44px"
                      py={0}
                      pr={0}
                      textAlign="left"
                      borderRadius="10px"
                      _focusVisible={{ boxShadow: 'none', outline: '2px solid', outlineColor: ff.ink }}
                      aria-label={
                        line.type === ''
                          ? 'Content type, not selected. Open to choose.'
                          : `Content type, ${lineLabel(line.type)}. Open to change.`
                      }
                    >
                      <HStack spacing="6px" align="center" minW={0} flex="1">
                        {line.type !== '' &&
                          (line.platform === 'instagram' ? (
                            <InstagramPlatformChip />
                          ) : line.platform === 'tiktok' ? (
                            <TikTokPlatformChip />
                          ) : (
                            <UgcPackageIcon />
                          ))}
                        <Text
                          fontSize="15px"
                          fontWeight={line.type === '' ? 400 : 500}
                          color={line.type === '' ? ff.muted : ff.ink}
                          isTruncated
                          flex="1"
                          minW={0}
                        >
                          {line.type === '' ? 'Select content type…' : lineLabel(line.type)}
                        </Text>
                        <ChevronDownIcon color={ff.text2} flexShrink={0} />
                      </HStack>
                    </MenuButton>
                    <MenuList {...PACKAGE_CONTENT_TYPE_MENU_LIST_PROPS}>
                      <PackageContentTypeMenuItems
                        selectedType={line.type === '' ? undefined : line.type}
                        onSelect={(opt) => handleTypeSelect(line.id, opt.type)}
                      />
                    </MenuList>
                  </Menu>
                </Box>
                <HStack spacing="6px" flexShrink={0} align="center">
                  <HStack
                    h="40px"
                    w={isCarouselType(line.type) ? '124px' : '104px'}
                    borderRadius="full"
                    bg={ff.grey}
                    px="2px"
                    spacing={0}
                    opacity={line.type === '' ? 0.45 : 1}
                  >
                    <IconButton
                      size="sm"
                      variant="ghost"
                      h="36px"
                      w="36px"
                      minW="36px"
                      borderRadius="full"
                      aria-label={isCarouselType(line.type) ? 'Fewer photos' : 'Decrease quantity'}
                      icon={<MinusIcon boxSize="12px" />}
                      onClick={() => bumpQty(line.id, -1)}
                      color={ff.ink}
                      isDisabled={line.type === ''}
                      _hover={{ bg: 'white' }}
                    />
                    <Text flex="1" fontFamily="display" fontSize="14px" fontWeight={600} textAlign="center" color={ff.ink}>
                      {line.type === ''
                        ? '—'
                        : isCarouselType(line.type)
                          ? `${line.count ?? 2} photos`
                          : line.quantity}
                    </Text>
                    <IconButton
                      size="sm"
                      variant="ghost"
                      h="36px"
                      w="36px"
                      minW="36px"
                      borderRadius="full"
                      aria-label={isCarouselType(line.type) ? 'More photos' : 'Increase quantity'}
                      icon={<SmallAddIcon boxSize="16px" />}
                      onClick={() => bumpQty(line.id, 1)}
                      color={ff.ink}
                      isDisabled={line.type === ''}
                      _hover={{ bg: 'white' }}
                    />
                  </HStack>
                  {lines.length > 1 && (
                    <IconButton
                      aria-label="Remove content type"
                      size="sm"
                      variant="ghost"
                      w="36px"
                      h="36px"
                      minW="36px"
                      borderRadius="full"
                      color={ff.muted}
                      _hover={{ bg: ff.grey, color: ff.ink }}
                      icon={<CloseIcon w="2.5" h="2.5" />}
                      onClick={() => removeLine(line.id)}
                    />
                  )}
                </HStack>
              </Flex>
            ))}
            <Menu placement="bottom-start" matchWidth isLazy>
              <MenuButton
                as={Button}
                w="100%"
                h="48px"
                variant="outline"
                borderRadius="14px"
                borderStyle="dashed"
                borderColor={ff.border}
                bg={ff.grey}
                color={ff.ink}
                fontFamily="display"
                fontWeight={600}
                fontSize="14px"
                rightIcon={<ChevronDownIcon w="3.5" h="3.5" />}
                isDisabled={!canAddDeliverable}
                _hover={canAddDeliverable ? { bg: ff.line } : undefined}
                _disabled={{ opacity: 0.45, cursor: 'not-allowed' }}
              >
                <Box as="span" display="flex" alignItems="center" gap={2}>
                  <AddIcon w="3" h="3" />
                  Add another deliverable
                </Box>
              </MenuButton>
              <MenuList {...PACKAGE_CONTENT_TYPE_MENU_LIST_PROPS}>
                <PackageContentTypeMenuItems onSelect={(opt) => handleAddLine(opt.platform, opt.type)} />
              </MenuList>
            </Menu>
          </VStack>
        </Box>

        <Box>
          <HStack as="h4" mb="10px" spacing="6px" align="baseline">
            <Text textStyle="eyebrow" color={ff.text2}>
              2 · Description
            </Text>
            <Text as="span" fontSize="12px" color={ff.muted}>
              (optional)
            </Text>
          </HStack>
          <Textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            minH="100px"
            fontSize="15px"
            placeholder="e.g., Product showcase with trending audio, raw UGC footage included"
            resize="vertical"
            aria-label="Package description"
          />
        </Box>

        <Box>
          <Text as="h4" textStyle="eyebrow" color={ff.text2} mb="10px">
            3 · Price
          </Text>
          <InputGroup>
            <InputLeftElement h="100%" pointerEvents="none" color={ff.text2} fontSize="16px" pl="14px" w="32px">
              $
            </InputLeftElement>
            <Input
              pl="36px"
              fontSize="16px"
              fontWeight={500}
              color={ff.ink}
              inputMode="decimal"
              value={priceDigits}
              onChange={(e) => {
                const v = e.target.value.replace(/[^0-9.]/g, '');
                setPriceDigits(v === '' ? '' : v);
              }}
              placeholder="0"
              aria-label="Package price in dollars"
            />
          </InputGroup>
        </Box>
      </VStack>
    </ModalShell>
  );
};

export default CreatePackageModal;
